import { useEffect, useMemo, useState } from 'react';
import { MutationError } from '@elementary/common-ui/types/mutationTypes';
import { useMutation } from '@tanstack/react-query';
import { useForm, FormProvider } from 'react-hook-form';
import useInvalidateAlertsRelatedData from 'shared/hooks/useInvalidateAlertsRelatedData';
import useSnackbar from 'shared/hooks/useSnackbar';
import RuleDrawer from '../ruleDrawer/RuleDrawer';
import * as api from '../../services/api';
import { AlertRoutingRuleModel, AlertRuleForm, RuleType } from '../../types';
import {
  alertFilterModelsToAlertFilters,
  alertFiltersToAlertFilterModels,
} from '../../utils/filterConverters';

type Props = {
  alertRule?: AlertRoutingRuleModel;
  onClose: () => void;
  open: boolean;
  envId: string;
};

const AlertRuleDrawer = ({ alertRule, onClose, open, envId }: Props) => {
  const [errorMessage, setErrorMessage] = useState<string>();

  const { openSnackbar } = useSnackbar();
  const { invalidateAlertsDataQueries } = useInvalidateAlertsRelatedData();

  const isEditMode = useMemo(() => alertRule?.id != null, [alertRule]);

  const defaultValues = useMemo(() => {
    const ruleTypeIncidents: RuleType =
      alertRule?.incident_filters && alertRule.incident_filters.length > 0
        ? 'filter'
        : 'all';

    const destinations =
      alertRule?.destinations?.map((d) => ({
        integrationId: d.alert_integration_id,
        destinationId: d.id,
        channelName: d.channel_name,
        type: d.alert_integration_type,
      })) || [];

    return {
      ruleId: alertRule?.id,
      name: alertRule?.name,
      ruleTypeIncidents: isEditMode ? ruleTypeIncidents : undefined,
      incidentFilters: alertFiltersToAlertFilterModels(
        alertRule?.incident_filters,
      ),
      assetFilters: alertFiltersToAlertFilterModels(alertRule?.asset_filters),
      alertCategories: alertRule?.alert_categories,
      destinations,
    };
  }, [alertRule, isEditMode]);

  const methods = useForm<AlertRuleForm>({ defaultValues, mode: 'onChange' });

  const { mutate: createAlertRule, isLoading: isCreateAlertRuleLoading } =
    useMutation(
      (data: AlertRuleForm) => {
        const reqData = {
          name: data.name,
          asset_filters: data.assetFilters?.length
            ? alertFilterModelsToAlertFilters(data.assetFilters)
            : [],
          incident_filters:
            data.ruleTypeIncidents === 'filter'
              ? alertFilterModelsToAlertFilters(data.incidentFilters)
              : [],
          alert_categories: data.alertCategories,
          destinations: data.destinations.map((d) => ({
            channel_name: d.channelName,
            alert_integration_id: d.integrationId,
          })),
        };
        return api.createAlertRoutingRule(reqData, envId!);
      },
      {
        onSuccess: () => {
          onClose();
          invalidateAlertsDataQueries();
          openSnackbar({
            message: 'Alert rule created successfully!',
            variant: 'success',
          });
        },
        onError: (error: MutationError) => {
          setErrorMessage(
            (error.response?.data?.error_message as string) || 'Error',
          );
        },
      },
    );

  const { mutate: updateAlertRule, isLoading: isUpdateAlertRuleLoading } =
    useMutation(
      (data: AlertRuleForm) => {
        const reqData = {
          name: data.name,
          asset_filters: data.assetFilters?.length
            ? alertFilterModelsToAlertFilters(data.assetFilters)
            : [],
          incident_filters:
            data.ruleTypeIncidents === 'filter'
              ? alertFilterModelsToAlertFilters(data.incidentFilters)
              : [],
          alert_categories: data.alertCategories,
          destinations: data.destinations.map((d) => ({
            id: d.destinationId,
            channel_name: d.channelName,
            alert_integration_id: d.integrationId,
          })),
        };
        return api.updateAlertRoutingRule(reqData, envId!, data.ruleId!);
      },
      {
        onSuccess: () => {
          onClose();
          invalidateAlertsDataQueries();
          openSnackbar({
            message: 'Alert rule updated successfully!',
            variant: 'success',
          });
        },
        onError: (error: MutationError) => {
          setErrorMessage(
            (error.response?.data?.error_message as string) || 'Error',
          );
        },
      },
    );

  useEffect(() => {
    if (open) {
      methods.reset?.(defaultValues);
      setErrorMessage(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const onSubmit = (newData: AlertRuleForm) => {
    if (isEditMode) {
      updateAlertRule(newData);
    } else {
      createAlertRule(newData);
    }
  };

  return (
    <FormProvider {...methods}>
      <RuleDrawer
        open={open}
        onClose={onClose}
        envId={envId}
        title="alert rule"
        namePlaceholder="e.g. Failure alerts"
        onSubmit={methods.handleSubmit(onSubmit)}
        isLoading={
          isEditMode ? isUpdateAlertRuleLoading : isCreateAlertRuleLoading
        }
        isEditMode={isEditMode}
        errorMessage={errorMessage}
        showCadence={false}
      />
    </FormProvider>
  );
};

export default AlertRuleDrawer;
